import React, { useEffect, useRef } from "react";
import "./PhotoUpload.scss";
import { ProgressSpinner } from "primereact/progressspinner";
import { PhotoUploadViewModel } from "@/features/settings/viewmodel/PhotoUploadViewModel";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { imageList } from "@/core/utils/imageList";

interface PhotoUploadProps {
  src: string;
  onChange: (fid: string) => void; // Now expects FID, not URL
  inline?: boolean;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  src,
  onChange,
  inline,
}) => {
  const {
    fileInputRef,
    avatarUrl,
    onUploadClick,
    onFileChange,
    isUploading,
    error,
  } = PhotoUploadViewModel(src);

  // Use ref to track if we've already notified about this FID
  const lastNotifiedFid = useRef<string>("");

  // Handle file change and notify parent with FID
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const fid = await onFileChange(e);
      if (fid && fid !== lastNotifiedFid.current) {
        lastNotifiedFid.current = fid;
        onChange(fid); // Send FID to parent
      }
    } catch (err) {
      // Error already handled in viewmodel
      console.error("File upload failed:", err);
    }
  };

  // Check if avatarUrl is valid
  const hasValidAvatar =
    avatarUrl && !(Array.isArray(avatarUrl) && avatarUrl.length === 0);

  const renderUploadArea = () => (
    <div className="photo-upload flex align-items-center gap-4">
      <div className="avatar-container" style={{ position: "relative" }}>
        {hasValidAvatar ? (
          <img className="photo-preview" src={avatarUrl} alt="avatar" />
        ) : (
          <div
            className="photo-preview placeholder"
            style={{
              backgroundColor: "#d1d5db",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BgImage img={imageList.noimage} />
          </div>
        )}

        {isUploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "50%",
            }}
          >
            <ProgressSpinner
              style={{ width: "40px", height: "40px" }}
              strokeWidth="4"
            />
          </div>
        )}
      </div>

      <div className="photo-actions">
        <button
          type="button"
          className="btn-upload mb-2"
          onClick={onUploadClick}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Click to upload"}
        </button>

        <p className="upload-info text-sm text-gray-500 m-0">
          SVG, PNG, JPG or GIF (max. 5MB)
        </p>

        {error && <p className="text-sm text-red-500 mt-2 m-0">{error}</p>}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={isUploading}
      />
    </div>
  );

  if (inline) {
    return <div className="after-name-photo">{renderUploadArea()}</div>;
  }

  return (
    <div className="form-row align-items-start">
      <div className="form-group">
        <label>
          Your photo (optional)
          <p className="form-description">
            This will be displayed on your profile.
          </p>
        </label>
        {renderUploadArea()}
      </div>
    </div>
  );
};
